/**
 * M-1 — the OAuth 2.1 / DCR consent screen.
 *
 * `OAuthAsService.beginAuthorize` redirects an authenticated operator to `/oauth/consent?request=<id>`;
 * this is the page that answers. Without it a client completes dynamic registration, hits
 * `/authorize`, and lands on the CP gateway's `/` catch-all — an **HTTP 200 of `index.html`**, a
 * dead end that looks like success (recorded as R45-01).
 *
 * ## ⛔ THIS PAGE RENDERS ATTACKER-CONTROLLED TEXT
 *
 * `clientName` is **200 characters of attacker-controlled input, stored verbatim**: RFC 7591
 * dynamic registration is open, and `register-client.service.ts` deliberately does not sanitise the
 * name — a pinned test states that sanitising *there* would imply something downstream trusts it.
 * **This page is that downstream.** Consent-screen spoofing — a self-registered client calling
 * itself `Zion Platform (Official)` — is the classic DCR hole, so five properties are structural
 * here rather than cosmetic. They are carried from the old page verbatim and each is a test
 * obligation:
 *
 *  - **S-1 Inert text.** `clientName`, `clientId` and `redirectHost` are rendered as plain JSX
 *    children only. No `dangerouslySetInnerHTML`, no markdown, no HTML interpretation, ⛔ never
 *    interpolated into an `aria-label` or a `title`, ⛔ never used to build an `href`/`src`/`style`.
 *    ⚠️ The scope SLUG is server-supplied too and gets the same treatment.
 *  - **S-2 `dynamic` is marked unverified** — icon + words, never colour alone, never a tooltip.
 *    `admin` renders **no marker at all**: the absence of a claim, not a "verified" badge, because
 *    a badge is the next thing to spoof.
 *  - **S-3 The redirect host is co-equal with the name** — same block, same typographic weight
 *    ({@link IDENTITY_STRONG_CLASS}, one constant so the two cannot drift), immediately below it.
 *    **The host is the part an attacker cannot forge; the name is the part they can**, so the two
 *    must never be presented as though the name were the stronger signal.
 *  - **S-4 `break-all`, and ⛔ no truncation anywhere in the identity block.** A 200-character
 *    unbroken run wraps inside its own block and cannot displace anything below it. Truncation is a
 *    security property on this page, not a nicety — a truncated host is a forgeable host.
 *  - **S-5 Approve is not autofocused and is blocked when any scope is unheld.** `autoFocusTitle`
 *    puts arrival focus on the `<h1>`, never on a decision control: the operator must aim. The
 *    server lists in `scopes` only what Approve grants, so a scope there is unheld only when the
 *    operator holds none. The requested scopes it does not grant come in `notGranted`, and the
 *    page names them in their own list, so the operator sees what the client asked for.
 *
 * ## The passkey elevation (plan 2026-10-28-identity-mcp-tools, R-020 item 4)
 *
 * An elevated-only scope, such as a write string of the identity MCP server, reaches a grant only
 * when the approving session acts as `PLATFORM_ADMIN_ROLE` with a live elevation. The view names
 * such scopes in `elevationRequired`. The page then marks each of them in its list, and it offers
 * the `role-elevation` step-up through the `elevate` prop: the passkey dialog of
 * `StepUpProvider`, then a new read of the view. The field is a hint. The approve of cp-auth-api
 * checks the elevation again, and an approve without it grants the other scopes alone. Approve and
 * Deny stay disabled while the elevation runs, so no decision goes out under a role that is about
 * to change.
 *
 * The view is true for the session that read it. The `authority` prop names that session: its
 * operator, its active role and the end of its elevation. The page keys each view with the
 * request id, those three values, whether the elevation is still live on the page clock, and the
 * switch epoch. A render whose key differs from the key of the view shows the pending state, so the
 * old card is gone in the same render as the change, before the new read starts. The changes are a
 * switch, an elevation end that passes (the page times it itself, before `ActiveRoleProvider`
 * refreshes the session), a sign-in as another operator or another role, and a move of the URL to
 * another request. Approve also checks the elevation end on the page clock when pressed.
 *
 * A decision belongs to the request it was pressed on. Only that request shows Approve and Deny
 * disabled while the decision runs, so a stalled decision of an earlier request never blocks a
 * later one. A decision that ends after the URL moved to another request changes nothing on the
 * page: a success opens no redirect, and a failure shows no notice. A failure of the request still
 * in the URL, under the same operator, shows also when a new read of that request started since
 * the press (the elevation end on the page clock starts one). A transport failure keeps its notice
 * for the card of that read. A refusal of the server (`classifyConsentFailure` answers a terminal
 * kind) replaces that read. The page clock and the server clock can differ: an elevation can end
 * after the press and before cp-auth-api decides. The server then decides alone. It grants the
 * delegable part, or it refuses with 403 `invalid_scope` when that part is empty, and the page shows
 * that refusal.
 *
 * ## Collaborators
 *
 * Every collaborator is a prop (the `OAuthCallbackPage` idiom) so the tests drive real behavioural
 * fakes — no `vi.fn`, no `vi.mock`.
 *
 * ⛔ **`scope.held` is rendered exactly as the SERVER computed it and is never re-derived from
 * `useHasPermission`.** The three routes carry no `@RequirePermission` precisely because the
 * operator's authority is resolved from the DATABASE inside `getAuthorizeView` — *"the token may be
 * up to one access-token TTL stale, and consent must reflect CURRENT authority"*. A client-side
 * re-derivation would consult the stale claims this design exists to bypass.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AuthCardFrame, AuthPendingState } from '../shell/AuthCardFrame';
import { Button } from '../shell/Button';
import { ButtonLink } from '../shell/ButtonLink';
import { PLATFORM_ADMIN_ROLE, type CpAuthorizeRequestView, type CpConsentScopeView } from '@zion/sdk-contracts';
import { DataValue } from '../shell/data-tone';
import { elevationName, type ActiveRoleValue, type RoleSwitchResult } from '../shell/active-role-context';
import { stashReturnTo as stashReturnToDefault } from '../platform/auth/return-to';
import {
  classifyConsentFailure,
  type ConsentDecisionResult,
  type TerminalKind,
} from '../api/oauthConsent.api';

/**
 * One passkey elevation of the session to `PLATFORM_ADMIN_ROLE`: the `switchRole` of
 * `ActiveRoleProvider`, bound to that role. It answers how the switch ended and never throws.
 */
export type ConsentElevate = () => Promise<RoleSwitchResult>;

/**
 * The elevation the consent page can offer, from the active-role value of the session.
 *
 * `ConsentGate` of `CpAdminApp.tsx` calls it with `useActiveRole()`. The answer for each input:
 * - `null` (no provider, or a token with no `heldSiteRoles` claim): `null`;
 * - a session that does not hold `PLATFORM_ADMIN_ROLE`: `null`, because the switch route
 *   refuses the role (`role_not_held`);
 * - a session whose default role is `PLATFORM_ADMIN_ROLE`: `null`, because a switch to the active
 *   role with no elevation is `unchanged` (`classifyActiveRoleSwitch`), so no passkey can elevate it;
 * - any other session: a function that runs `switchRole(PLATFORM_ADMIN_ROLE)`.
 *
 * Pure: it reads the value and calls nothing.
 */
export function consentElevateOf(activeRole: ActiveRoleValue | null): ConsentElevate | null {
  if (activeRole === null) return null;
  if (!activeRole.heldSiteRoles.includes(PLATFORM_ADMIN_ROLE)) return null;
  if (activeRole.restRole === PLATFORM_ADMIN_ROLE) return null;
  return () => activeRole.switchRole(PLATFORM_ADMIN_ROLE);
}

/**
 * The session authority that a consent view was read under: the operator, the active role and the
 * end of the elevation, epoch ms. The page reads the view again when any of the three changes.
 */
export interface ConsentAuthority {
  /**
   * The operator id of the session, or `null` when the page knows none. Two operators with the
   * same role and no elevation hold different scopes, so the operator is part of the authority.
   */
  readonly operatorId: string | null;
  /** The active site role of the session, or `null` when the token names none. */
  readonly activeRole: string | null;
  /** The end of the elevation, epoch ms, or `null` for a session at rest. */
  readonly elevatedUntilMs: number | null;
}

/**
 * The authority of the session.
 *
 * `ConsentGate` of `CpAdminApp.tsx` calls it beside `consentElevateOf`, with the operator id of
 * `useAuth()` and the active-role value of `useActiveRole()`. The operator id comes from the auth
 * state, so it is present also when no provider value exists. `null` for `activeRole` (no provider,
 * or a token with no `heldSiteRoles` claim) answers `{ operatorId, activeRole: null,
 * elevatedUntilMs: null }`. Any other value answers `operatorId`, its `activeRole` and its
 * `elevatedUntilMs`. Pure: it reads the values and calls nothing.
 */
export function consentAuthorityOf(operatorId: string | null, activeRole: ActiveRoleValue | null): ConsentAuthority {
  if (activeRole === null) return { operatorId, activeRole: null, elevatedUntilMs: null };
  return { operatorId, activeRole: activeRole.activeRole, elevatedUntilMs: activeRole.elevatedUntilMs };
}

/** The largest delay that `setTimeout` keeps: a longer one fires at once. */
const MAX_TIMER_MS = 2_147_483_647;

/** The key of the view that `authority` reads for `requestId`: see the module docblock. */
function viewKeyOf(
  requestId: string | null,
  authority: ConsentAuthority,
  elevationLive: boolean,
  switchEpoch: number,
): string {
  return JSON.stringify([requestId, authority.operatorId, authority.activeRole, authority.elevatedUntilMs, elevationLive, switchEpoch]);
}

/** Do `a` and `b` name the same operator, active role and elevation end? */
function sameAuthority(a: ConsentAuthority, b: ConsentAuthority): boolean {
  return a.operatorId === b.operatorId && a.activeRole === b.activeRole && a.elevatedUntilMs === b.elevatedUntilMs;
}

/** How one read of the view, or one decision that ended the request, settled. */
type ReadOutcome =
  | { readonly kind: 'view'; readonly view: CpAuthorizeRequestView }
  | { readonly kind: 'terminal'; readonly terminal: TerminalKind }
  | { readonly kind: 'transport' };

/** A settled read, with the view key it was read under. */
interface SettledRead {
  readonly key: string;
  readonly outcome: ReadOutcome;
}

/** The notice of state 7, a load that failed in transport. */
const LOAD_FAILED_TEXT = 'Could not load the authorization request.';

/** The name of the elevated role in the page copy: "Admin". */
const ELEVATED_NAME = elevationName(PLATFORM_ADMIN_ROLE);

/** The marker on a requested scope that needs the elevation. */
export const ELEVATION_MARKER_TEXT = `Needs ${ELEVATED_NAME} with a passkey`;

/** The note of the elevation block when the page can offer the elevation. */
export const ELEVATION_OFFER_TEXT = `The marked permissions need you to act as ${ELEVATED_NAME}, which your passkey confirms. Without it, Approve does not grant them.`;

/** The note of the elevation block when this session cannot elevate. */
export const ELEVATION_UNAVAILABLE_TEXT = `The marked permissions need you to act as ${ELEVATED_NAME}, and this session cannot. Approve does not grant them.`;

/** The label of the elevation button. */
export const ELEVATE_BUTTON_TEXT = `Act as ${ELEVATED_NAME}`;

/** The notice after a passkey step that ended without an approval. */
export const ELEVATION_NOT_VERIFIED_TEXT = `The passkey step did not approve the switch to ${ELEVATED_NAME}.`;

/** The notice after a switch that failed, with the reason of `failureMessage`. */
export function elevationFailedText(message: string): string {
  return `Could not act as ${ELEVATED_NAME}. ${message}`;
}

export interface OAuthConsentPageProps {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly loadRequest: (id: string) => Promise<CpAuthorizeRequestView>;
  readonly approve: (id: string) => Promise<ConsentDecisionResult>;
  readonly deny: (id: string) => Promise<ConsentDecisionResult>;
  /** Seam for the top-level navigation the OAuth redirect requires. */
  readonly navigateExternal: (url: string) => void;
  /**
   * Records where the visitor was trying to go, before the sign-in interstitial sends them away.
   *
   * ⛔ **Injected, defaulting to the real one** — the `OAuthCallbackPage.takeReturnTo` idiom: *"a
   * plain closure, never `vi.fn()`"*. It also makes this page's obligation assertable
   * **independently of `BASENAME`**, which matters more here than it looks:
   * `isSafeReturnPath` ends with `value === BASENAME || value.startsWith(\`${BASENAME}/\`)`, and
   * with `BASENAME === '/cp-admin'` a browser path of `/oauth/consent?request=…` **fails that check
   * and is silently not stashed**. At the R-2 flip `BASENAME` becomes `''`, the predicate degrades
   * to *"any absolute path"* (the `//` and `\\` arms still guarding) and the stash starts working —
   * with no edit here, to `return-to.ts`, or to any test.
   *
   * ⇒ Accepted, not a defect: pre-flip the console is not served at root at all, so the producer's
   * `/oauth/consent` redirect never reaches this page. The two facts change together in one PR.
   *
   * ⭐ **And it is exactly why this is a PROP.** A test asserting the `sessionStorage` key would be
   * green today *for the wrong reason* and would flip colour at the R-2 flip — a verdict that
   * depends on a constant in another workstream. *"The page invoked its stash seam exactly once"* is
   * `BASENAME`-agnostic and survives the flip; the real function's basename behaviour is covered by
   * `platform/auth/__tests__/return-to.test.ts`, which is where it belongs.
   */
  readonly stashReturnTo?: () => void;
  /**
   * The passkey elevation to `PLATFORM_ADMIN_ROLE` (R-020 item 4), or `null` when this session
   * cannot elevate (`consentElevateOf`). The page calls it only when the view names an
   * `elevationRequired` entry and the person presses the elevation button.
   *
   * Required, with no default, so that every render names the arm it is about.
   */
  readonly elevate: ConsentElevate | null;
  /**
   * The authority of the session now (`consentAuthorityOf`). A change of any value drops the view
   * and reads it again for the request in the URL. Required, with no default, for the reason
   * `elevate` gives.
   */
  readonly authority: ConsentAuthority;
  /**
   * The page clock, epoch ms: the clock of `ActiveRoleProvider` in production (`now` of the
   * active-role value), so the page and the provider time the elevation end alike. Default
   * `Date.now`. The page reads it to time the end of `authority.elevatedUntilMs`, and again when
   * Approve or Deny is pressed.
   */
  readonly now?: () => number;
}

/**
 * 8-4-4-4-12 hex.
 *
 * ⚠️ Deliberately LOOSER than the server's `z.string().uuid()` (no version/variant pinning): the
 * server is authoritative, and a client-side filter STRICTER than the server would reject ids the
 * server would have accepted. This exists to keep a hostile `?request=` out of the request line, not
 * to re-implement the server's validation.
 */
const REQUEST_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * ⛔⛔ **THE S-3 CO-EQUALITY, AS ONE CONSTANT — this is the security property in code form.**
 *
 * The client NAME and the redirect HOST both carry this and nothing else weight-bearing, so they
 * cannot drift apart into "the name looks stronger than the host". ⚠️ Writing the tokens twice would
 * make co-equality a coincidence that holds until someone tunes one of the two; a shared constant
 * makes it structural. `break-all` is in here for the same reason (S-4) — the two fields that wrap
 * are exactly the two an attacker controls.
 */
const IDENTITY_STRONG_CLASS = 'break-all text-base font-semibold text-primary';

/**
 * The client id — deliberately QUIETER than the name and host.
 *
 * ⚠️ It is attacker-controlled too and so carries `break-all`, but it is an opaque identifier
 * rather than a claim about who is asking, and giving it the same weight would dilute the two
 * fields the operator is meant to compare.
 */
const IDENTITY_MUTED_CLASS = 'mt-1 break-all font-mono text-xs text-muted';

/**
 * One requested permission.
 *
 * ⛔ A real `<ul>`/`<li>`, **not** a `ScrollTable`. That primitive is for record regions with
 * sorting, filtering and an emptiness contract; three permission slugs inside a card is none of
 * those. (`COMPONENT-LIBRARY.md` §4.2's argument against a convenience wrapper, applied in reverse.)
 */
function ScopeRow({
  scope,
  needsElevation,
}: {
  readonly scope: CpConsentScopeView;
  /** The slug is an `elevationRequired` entry. Its marker then takes the place of the unheld one. */
  readonly needsElevation: boolean;
}): React.JSX.Element {
  return (
    <li className="flex items-start gap-2" data-testid="consent-scope">
      <div className="min-w-0">
        {/* ⚠️ Server-supplied, so it gets S-1's inert-text and S-4's `break-all` treatment too. */}
        <code className="break-all rounded bg-surface-muted px-1 py-0.5 font-mono text-xs text-primary">
          {scope.slug}
        </code>
        {needsElevation ? (
          <ElevationMarker />
        ) : scope.held ? null : (
          <DataValue verdict="caution" testId="consent-scope-unheld" className="mt-0.5">
            You don&apos;t have this permission
          </DataValue>
        )}
      </div>
    </li>
  );
}

/** The marker of a requested scope that only an elevated session grants: icon and words. */
function ElevationMarker(): React.JSX.Element {
  return (
    <DataValue verdict="caution" testId="consent-scope-elevation" className="mt-0.5">
      {ELEVATION_MARKER_TEXT}
    </DataValue>
  );
}

export function OAuthConsentPage({
  isAuthenticated,
  isLoading,
  loadRequest,
  approve,
  deny,
  navigateExternal,
  stashReturnTo = stashReturnToDefault,
  elevate,
  authority,
  now = Date.now,
}: OAuthConsentPageProps): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const rawRequestId = searchParams.get('request');
  const requestId = rawRequestId !== null && REQUEST_ID_RE.test(rawRequestId) ? rawRequestId : null;
  const { operatorId, activeRole, elevatedUntilMs } = authority;

  /*
   * The last read that settled, with the key it was read under (`viewKeyOf`). A render shows it
   * only while its key is the key of that render, so a view, a dead end or a load failure of an
   * earlier request or session never shows under a later one.
   */
  const [read, setRead] = useState<SettledRead | null>(null);
  /** The notice of the last decision that failed in transport, or `null`. */
  const [transportError, setTransportError] = useState<string | null>(null);
  /*
   * ⛔ Initialised `true`, not `false`. The first paint happens before the load effect has run.
   * Starting busy is also simply true: the page's job on arrival is to fetch.
   */
  const [loadingView, setLoadingView] = useState(true);
  /** The request id of the decision in flight, or `null`. Only that request shows the decision busy. */
  const [submittingFor, setSubmittingFor] = useState<string | null>(null);
  /** An elevation is in flight: the passkey dialog is open, or the switch is on the wire. */
  const [elevating, setElevating] = useState(false);
  /** The notice of the last elevation that did not switch, or `null`. */
  const [elevationNotice, setElevationNotice] = useState<string | null>(null);
  /*
   * One more for each switch that no read under the new session followed yet. It is part of the
   * view key, so such a switch drops the view and reads it again.
   */
  const [switchEpoch, setSwitchEpoch] = useState(0);
  /*
   * The elevation end that the page clock saw pass, or `null`. An end that passed already at the
   * first render counts from that render, so the page does not read twice on arrival.
   */
  const [lapsedUntil, setLapsedUntil] = useState<number | null>(() =>
    elevatedUntilMs !== null && now() >= elevatedUntilMs ? elevatedUntilMs : null,
  );
  /* The number of reads started. A read applies its answer only while it is the latest one. */
  const loadSeq = useRef(0);
  /*
   * The authority of the latest read that started. Before the first read it holds the authority of
   * the first render, which is the authority of that read.
   */
  const lastReadAuthority = useRef<ConsentAuthority>({ operatorId, activeRole, elevatedUntilMs });

  const elevationLive = elevatedUntilMs !== null && lapsedUntil !== elevatedUntilMs;
  const viewKey = viewKeyOf(requestId, authority, elevationLive, switchEpoch);

  /*
   * The request in the URL, the operator and the view key of the last commit. A decision reads it
   * when it ends, so it acts on the page as it is then and not as it was at the press.
   */
  const pageNow = useRef({ requestId, operatorId, viewKey });
  useLayoutEffect(() => {
    pageNow.current = { requestId, operatorId, viewKey };
  });

  const showSignIn = !isLoading && !isAuthenticated;

  /*
   * ⛔ Guarded on `showSignIn`, which is `!isLoading && !isAuthenticated` — BOTH halves are
   * load-bearing and each has its own inversion control in the tests. Stashing while
   * `isAuthenticated` records a destination the operator is already at; stashing while `isLoading`
   * records one for a session that turns out to be signed in. A page that stashed on every render
   * would pass a bare "was it called?" assertion.
   */
  useEffect(() => {
    if (!showSignIn) return;
    stashReturnTo();
  }, [showSignIn, stashReturnTo]);

  /*
   * The end of the elevation on the page clock. The provider refreshes the session one second
   * after the end; the view stops counting the elevation at the end itself, as cp-auth-api does.
   * A delay longer than `setTimeout` keeps arms the timer again until the end.
   */
  useEffect(() => {
    if (elevatedUntilMs === null || lapsedUntil === elevatedUntilMs) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = (): void => {
      const left = elevatedUntilMs - now();
      if (left <= 0) setLapsedUntil(elevatedUntilMs);
      else timer = setTimeout(check, Math.min(MAX_TIMER_MS, left));
    };
    check();
    return () => clearTimeout(timer);
  }, [elevatedUntilMs, lapsedUntil, now]);

  /*
   * One read of the view under `key`. It drops the last read, the elevation notice and the
   * decision notice first, so the page never shows a view, a dead end or a notice of an earlier
   * request or session while the read runs. A read that a later read overtook applies nothing:
   * the answer of the old request or the old session never replaces the newer one.
   */
  const loadView = useCallback(
    async (id: string, key: string, readAuthority: ConsentAuthority): Promise<void> => {
      loadSeq.current += 1;
      const seq = loadSeq.current;
      lastReadAuthority.current = readAuthority;
      setRead(null);
      setElevationNotice(null);
      setTransportError(null);
      setLoadingView(true);
      try {
        const next = await loadRequest(id);
        if (seq !== loadSeq.current) return;
        setRead({ key, outcome: { kind: 'view', view: next } });
      } catch (err) {
        if (seq !== loadSeq.current) return;
        const kind = classifyConsentFailure(err);
        setRead({ key, outcome: kind === 'transport' ? { kind: 'transport' } : { kind: 'terminal', terminal: kind } });
      }
      setLoadingView(false);
    },
    [loadRequest],
  );

  /*
   * The read of the request in the URL, under the session of `authority`. It runs again when the
   * view key changes: the request id, the operator, the active role, the elevation end, the end
   * passing on the page clock, or the switch epoch.
   */
  useEffect(() => {
    if (isLoading || !isAuthenticated || requestId === null) return;
    void loadView(requestId, viewKey, { operatorId, activeRole, elevatedUntilMs });
  }, [isLoading, isAuthenticated, requestId, loadView, viewKey, operatorId, activeRole, elevatedUntilMs]);

  async function submitDecision(decision: 'approve' | 'deny', id: string): Promise<void> {
    /*
     * The view counted an elevation that the page clock says has ended: the approve would grant
     * less than the card says. Mark the end, which changes the view key, and read the view again.
     */
    if (decision === 'approve' && elevationLive && elevatedUntilMs !== null && now() >= elevatedUntilMs) {
      setLapsedUntil(elevatedUntilMs);
      return;
    }
    const pressOperator = operatorId;
    setSubmittingFor(id);
    setTransportError(null);
    let result: ConsentDecisionResult;
    try {
      result = decision === 'approve' ? await approve(id) : await deny(id);
    } catch (err) {
      /*
       * ⛔ **HERE, ON FAILURE ONLY — never in a `finally`.** On success the browser is leaving the
       * origin; clearing the busy state would re-enable both buttons for the frame between the call
       * and the navigation, which is a double-approve window on a ONE-SHOT authorization request.
       */
      setSubmittingFor((busyFor) => (busyFor === id ? null : busyFor));
      decisionFailed(decision, id, pressOperator, err);
      return;
    }
    /*
     * The URL moved to another request while the decision ran: the page shows that request, and
     * the redirect of the old one would leave it. The old request stays decided, so its controls
     * stay disabled.
     */
    if (pageNow.current.requestId !== id) return;
    /*
     * ⛔ A TOP-LEVEL navigation to a third-party origin — it cannot go through the router, which
     * is why this is a seam rather than a `useNavigate`.
     */
    navigateExternal(result.redirectTo);
  }

  /*
   * A decision on `id` by `pressOperator` failed. The URL names another request, or another
   * operator holds the session: the page shows that, and the failure says nothing about it. Else a
   * transport failure names the action in the notice, also over a read of the same request that
   * started since the press: that read clears no notice that comes after its start. A refusal of
   * the server is the state of the request, so it replaces the last read and drops a read still in
   * flight.
   */
  function decisionFailed(decision: 'approve' | 'deny', id: string, pressOperator: string | null, err: unknown): void {
    const page = pageNow.current;
    if (page.requestId !== id || page.operatorId !== pressOperator) return;
    const kind = classifyConsentFailure(err);
    if (kind === 'transport') {
      setTransportError(
        decision === 'approve'
          ? 'Could not approve the request. Check your connection and try again.'
          : 'Could not deny the request. Check your connection and try again.',
      );
      return;
    }
    loadSeq.current += 1;
    setRead({ key: page.viewKey, outcome: { kind: 'terminal', terminal: kind } });
    setLoadingView(false);
  }

  /*
   * One elevation, then a new read of the view. `run` never throws (`RoleSwitchResult`). A switch
   * reads the view again under the new session for the request that the URL names then, never for
   * the request of the press. When the latest read started under another authority than the one
   * of the press, the new session reached the page first and that read is the new one. Else the
   * switch epoch drops the view and starts the read, also when a read of another request started
   * under the old session while the passkey step ran. A cancel says nothing: the person closed the
   * dialog. Any other end names itself in the notice, unless a read started while the step ran:
   * that read is about another request or session, and the notice is not.
   */
  async function runElevation(run: ConsentElevate): Promise<void> {
    const pressAuthority: ConsentAuthority = { operatorId, activeRole, elevatedUntilMs };
    const seq = loadSeq.current;
    setElevating(true);
    setTransportError(null);
    setElevationNotice(null);
    const result = await run();
    setElevating(false);
    if (result.kind === 'switched') {
      if (sameAuthority(lastReadAuthority.current, pressAuthority)) setSwitchEpoch((n) => n + 1);
    } else if (seq !== loadSeq.current) {
      return;
    } else if (result.kind === 'not-verified') {
      setElevationNotice(ELEVATION_NOT_VERIFIED_TEXT);
    } else if (result.kind === 'failed') {
      setElevationNotice(elevationFailedText(result.message));
    }
  }

  // ── 1. Auth still resolving ───────────────────────────────────────────────────────────────────
  if (isLoading) return <AuthPendingState label="Loading…" testId="consent-loading" />;

  // ── 2. Unauthenticated ────────────────────────────────────────────────────────────────────────
  // ⛔ RENDERED, never `<Navigate>`: a redirect races `useAuth`'s mount refresh and bounces an
  // operator who is in fact signed in back to the login form.
  if (!isAuthenticated) {
    return (
      <AuthCardFrame
        title="Sign in to continue"
        subtitle="An application is asking for access to your account. Sign in to review the request."
        testId="consent-signin"
      >
        {/* A real anchor: `/login` has a URL, so right-click, middle-click and copy-link work. */}
        <ButtonLink to="/login" size="md" data-testid="consent-signin-link">
          Sign in
        </ButtonLink>
      </AuthCardFrame>
    );
  }

  /*
   * ⭐ States 3–5 are DEAD ENDS BY DESIGN — no retry affordance, because retrying cannot help. That
   * is why `AuthCardFrame.children` is optional (L-2): requiring a child here would invite a call
   * site to invent an action that does nothing, and an operator who presses it learns that our
   * controls are decorative.
   *
   * ⭐ **Verdict assignment is a rule, not a taste:** `bad` when the state was reached by the
   * operator's OWN action (5: their Approve was rejected; 7: their load/retry failed), `caution`
   * when it is the state of the world on arrival (3 and 4). `AuthCardFrame`'s `NOTICE_ROLE` then
   * makes exactly the right thing assertive. ⛔ `autoFocusTitle` is what announces the polite ones:
   * the old page instead put `role="alert"` on all three terminal cards, which announced a
   * caution-coloured card assertively — the two channels disagreed.
   */

  // ── 3. No usable request id ───────────────────────────────────────────────────────────────────
  if (requestId === null) {
    return (
      <AuthCardFrame
        title="This authorization request is not valid"
        notice={{
          verdict: 'caution',
          message:
            'The link is missing its request reference, or the reference is malformed. Return to the application and start again.',
        }}
        autoFocusTitle
        testId="consent-invalid"
      />
    );
  }

  // ── 6. The request is being fetched, or the last read is of another key (before 4 and 5) ──────
  // A key that moved shows this state in the same render as the move: the old card is gone before
  // the new read starts, so no press reaches a decision control of the old view.
  const current = !loadingView && read !== null && read.key === viewKey ? read.outcome : null;
  if (current === null) return <AuthPendingState label="Loading…" testId="consent-loading" />;

  // ── 4. Expired / already handled ──────────────────────────────────────────────────────────────
  if (current.kind === 'terminal' && current.terminal === 'invalid-or-expired') {
    return (
      <AuthCardFrame
        title="This request is no longer valid"
        notice={{
          verdict: 'caution',
          message:
            'This request has expired or was already used. Requests expire after 10 minutes. Return to the application and start again.',
        }}
        autoFocusTitle
        testId="consent-expired"
      />
    );
  }

  // ── 5. Approve rejected on the error CODE `invalid_scope` ─────────────────────────────────────
  if (current.kind === 'terminal') {
    return (
      <AuthCardFrame
        title="Can't approve this request"
        notice={{
          verdict: 'bad',
          message:
            "You don't have all the permissions this application is requesting. Ask a site administrator for access, or have the application request fewer permissions.",
        }}
        autoFocusTitle
        testId="consent-insufficient"
      />
    );
  }

  // ── 7. Transport failure on the LOAD — retryable, unlike every terminal above ─────────────────
  if (current.kind === 'transport') {
    return (
      <AuthCardFrame
        title="Couldn't load this request"
        notice={{ verdict: 'bad', message: LOAD_FAILED_TEXT }}
        testId="consent-load-failed"
      >
        <Button
          size="md"
          onClick={() => void loadView(requestId, viewKey, { operatorId, activeRole, elevatedUntilMs })}
          data-testid="consent-retry"
        >
          Retry
        </Button>
      </AuthCardFrame>
    );
  }

  const view = current.view;

  // ── 8. The consent card ───────────────────────────────────────────────────────────────────────
  const anyUnheld = view.scopes.some((s) => !s.held);
  const notGranted = view.notGranted;
  // Absent from a cp-auth-api built before R-020: absence means empty, which is exact there.
  const elevationRequired = view.elevationRequired ?? [];
  const busy = submittingFor === requestId || elevating;
  const cardNotice = transportError ?? elevationNotice;

  return (
    <AuthCardFrame
      title="Authorization request"
      /*
       * The SUBMIT failure lands in the same notice slot the terminal cards use, rather than in a
       * fifth hand-rolled error box. `bad` because the operator's own press produced it.
       */
      notice={cardNotice === null ? undefined : { verdict: 'bad', message: cardNotice }}
      /*
       * ⛔ Focus the TITLE on arrival — which is the same requirement as *"Approve is NOT
       * autofocused"*, seen from the other end. A card that arrives with focus on `Approve` can be
       * actioned by a keypress the operator had already begun for the previous screen.
       */
      autoFocusTitle
      testId="consent-card"
    >
      {/*
        ⛔ **THE IDENTITY BLOCK — page-local composition, argued and ACCEPTED (plan §5.3).** Exactly
        one adopter, no design-language reuse, and a security rule (S-3/S-4) nothing else shares. If
        a second consent-like surface ever appears, promote it then.

        Name and host live here TOGETHER and at the same weight. `break-all` on the
        attacker-controlled fields is what stops a 200-character unbroken name from overflowing its
        column; the host is a FOLLOWING SIBLING, so a wrapping name grows the block downward and can
        never push the host aside.
      */}
      <div
        className="min-w-0 rounded-lg border border-border-subtle bg-surface-muted px-4 py-3"
        data-testid="consent-identity"
      >
        <p className={IDENTITY_STRONG_CLASS} data-testid="consent-client-name">
          {view.clientName}
        </p>
        {view.registrationType === 'dynamic' ? (
          /*
           * ⛔ Icon + words (`DataValue` renders `VerdictGlyph` AND the children), so the claim
           * survives with colour removed and with images off. ⛔ Never a tooltip — it carries no
           * `title`, and the text is in the card's own `textContent` with no hover.
           */
          <DataValue verdict="caution" testId="consent-unverified" className="mt-1">
            Unverified application — anyone can register this name
          </DataValue>
        ) : null}
        <p className="mt-1 text-base text-primary" data-testid="consent-redirect-line">
          Will send you to{' '}
          <span className={IDENTITY_STRONG_CLASS} data-testid="consent-redirect-host">
            {view.redirectHost}
          </span>
        </p>
        <p className={IDENTITY_MUTED_CLASS} data-testid="consent-client-id">
          {view.clientId}
        </p>
      </div>

      <div>
        <p className="text-sm text-secondary" data-testid="consent-scopes-label">
          {notGranted.length > 0 ? 'Approve grants these permissions:' : 'It is asking for these permissions:'}
        </p>
        <ul className="mt-2 space-y-1.5">
          {view.scopes.map((scope) => (
            <ScopeRow key={scope.slug} scope={scope} needsElevation={elevationRequired.includes(scope.slug)} />
          ))}
        </ul>
      </div>

      {notGranted.length > 0 ? (
        <div data-testid="consent-not-granted">
          <p className="max-w-[100ch] text-sm text-secondary">
            It also asked for these permissions. You do not have them, so Approve does not grant them:
          </p>
          <ul className="mt-2 space-y-1.5">
            {notGranted.map((slug) => (
              <li key={slug} data-testid="consent-not-granted-scope">
                {/* Server-supplied, so it gets S-1's inert-text and S-4's `break-all` treatment too. */}
                <code className="break-all rounded bg-surface-muted px-1 py-0.5 font-mono text-xs text-muted">
                  {slug}
                </code>
                {elevationRequired.includes(slug) ? <ElevationMarker /> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/*
        The elevation block (R-020 item 4). It shows only when the view names a scope that needs
        the elevation. The copy is fixed text: no server string reaches it (S-1).
      */}
      {elevationRequired.length > 0 ? (
        <div className="space-y-2" data-testid="consent-elevation">
          <p className="max-w-[100ch] text-sm text-secondary" data-testid="consent-elevation-note">
            {elevate === null ? ELEVATION_UNAVAILABLE_TEXT : ELEVATION_OFFER_TEXT}
          </p>
          {elevate === null ? null : (
            <Button
              size="md"
              tone="default"
              onClick={() => void runElevation(elevate)}
              disabled={busy}
              data-testid="consent-elevate"
            >
              {ELEVATE_BUTTON_TEXT}
            </Button>
          )}
        </div>
      ) : null}

      {/* ⚠️ `max-w-[100ch]` below is the house MEASURE, and it must be a FONT-RELATIVE one. A named
          Tailwind size (`max-w-sm`…) is px-derived and silently changes its character count whenever
          the type ladder moves, which is why RATCHET A rejects it. */}
      {anyUnheld ? (
        <p className="max-w-[100ch] text-xs text-muted" data-testid="consent-blocked-note">
          Approval is unavailable because you don&apos;t have all the requested permissions.
        </p>
      ) : null}

      {/* ⛔ Equal width, and Approve is NOT autofocused — the operator must aim. */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          size="md"
          tone="default"
          onClick={() => void submitDecision('deny', view.id)}
          disabled={busy}
          data-testid="consent-deny"
        >
          Deny
        </Button>
        <Button
          size="md"
          tone="primary"
          onClick={() => void submitDecision('approve', view.id)}
          /*
           * ⛔ `anyUnheld` blocks Approve CLIENT-side purely so the operator is not invited into a
           * request the server will reject; the server rejects it regardless with
           * `403 {error:'invalid_scope'}`, which state 5 renders. This is an affordance, never the
           * control.
           */
          disabled={busy || anyUnheld}
          data-testid="consent-approve"
        >
          Approve
        </Button>
      </div>
    </AuthCardFrame>
  );
}
